import { ApiError } from '@clockwise/shared';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ImportSheet } from '@/components/custom/import-sheet';
import { holidaysApi } from '@/lib/api';
import { triggerDownload } from '@/lib/download';
import { queryKeys } from '@/lib/query-keys';

interface HolidayImportSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HolidayImportSheet({ open, onOpenChange }: HolidayImportSheetProps) {
  const queryClient = useQueryClient();

  return (
    <ImportSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Import holidays"
      description="Upload an .xlsx or .csv with Country, Date and Holiday columns. Rows are matched on those three, so re-importing a file updates instead of duplicating."
      onImport={async (file, { idempotencyKey }) => {
        const res = await holidaysApi.importFile(file, { idempotencyKey });
        return {
          totalRows: res.data.totalRows,
          created: res.data.created,
          updated: res.data.updated,
          failed: res.data.failed,
          rows: res.data.rows.map((r) => ({
            rowNumber: r.rowNumber,
            outcome: r.outcome,
            errors: r.errors,
          })),
        };
      }}
      onSuccess={() => {
        // Same prefix covers the list and the country filter — an import that
        // introduces a new country must repopulate the dropdown.
        queryClient.invalidateQueries({ queryKey: queryKeys.holidays.all });
        queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      }}
      onDownloadTemplate={async () => {
        try {
          const { blob, filename } = await holidaysApi.exportTemplate();
          triggerDownload(blob, filename ?? 'holidays-template.xlsx');
        } catch (err) {
          const msg = err instanceof ApiError ? err.message : 'Template download failed';
          toast.error(msg);
        }
      }}
    />
  );
}
